import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MicButton from '../voice/MicButton';

/* «Память»: lasting facts that every report, homework and the assistant keep in
   mind — about a child (studentId) or about the teacher (about="teacher").
   Dated kinds change the numbers: joined later — earlier lessons not counted;
   ill / away from–to — those absences are excused; left — later lessons not counted. */

export const KINDS = {
  health: {
    ru: 'Здоровье',
    en: 'Health',
    color: '#ef4444',
    hint: { ru: 'аллергия на орехи', en: 'nut allergy' },
  },
  joined: {
    ru: 'Пришёл в группу',
    en: 'Joined',
    color: '#2E7D5B',
    dates: 'from',
    hint: { ru: 'пришла в группу позже остальных', en: 'joined later' },
  },
  absence: {
    ru: 'Болел / отсутствовал',
    en: 'Ill / away',
    color: '#D98E04',
    dates: 'range',
    hint: { ru: 'болел, ангина', en: 'was ill' },
  },
  left: {
    ru: 'Ушёл',
    en: 'Left',
    color: '#64748b',
    dates: 'from',
    hint: { ru: 'перешёл в другую группу', en: 'moved to another group' },
  },
  note: {
    ru: 'Заметка',
    en: 'Note',
    color: '#3b82f6',
    hint: {
      ru: 'стесняется отвечать при всех',
      en: 'shy to answer in front of others',
    },
  },
  preference: {
    ru: 'Обо мне',
    en: 'About me',
    color: '#7A4988',
    hint: {
      ru: 'люблю короткие ответы; веду по вт и чт',
      en: 'I like short answers',
    },
  },
};

async function api(url, opts = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.message || `HTTP ${res.status}`);
  return j;
}

export function MemoryItem({ m, en, onDelete, showStudent }) {
  const K = KINDS[m.kind] || KINDS.note;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1,
        pl: 1.25,
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `4px solid ${K.color}`,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            mb: 0.25,
          }}
        >
          <Chip
            size="small"
            label={en ? K.en : K.ru}
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: alpha(K.color, 0.12),
              color: K.color,
            }}
          />
          {m.period && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {m.period}
            </Typography>
          )}
          {showStudent && m.student && (
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
              {m.student}
            </Typography>
          )}
          {m.source === 'assistant' && (
            <Tooltip
              title={
                en
                  ? 'Remembered by the assistant from a conversation'
                  : 'Запомнил AI-помощник из разговора'
              }
            >
              <AutoAwesomeIcon sx={{ fontSize: 14, color: '#E8A93D' }} />
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>
          {m.text}
        </Typography>
      </Box>
      {onDelete && (
        <Tooltip title={en ? 'Forget' : 'Забыть'}>
          <IconButton size="small" onClick={() => onDelete(m)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export default function MemoryPanel({
  studentId = null,
  about = 'student',
  en,
  title,
  compact = false,
}) {
  const teacher = about === 'teacher';
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [kind, setKind] = useState(teacher ? 'preference' : 'note');
  const [text, setText] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(
        await api(
          teacher
            ? '/api/memories?about=teacher'
            : `/api/memories?studentId=${studentId}`
        )
      );
    } catch (e) {
      setError(e.message);
    }
  }, [teacher, studentId]);
  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setError('');
    try {
      const m = await api('/api/memories', {
        method: 'POST',
        body: JSON.stringify({
          about: teacher ? 'teacher' : 'student',
          studentId,
          text,
          kind,
          from: from || null,
          to: to || null,
        }),
      });
      setItems((l) => [m, ...(l || [])]);
      setText('');
      setFrom('');
      setTo('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    setItems((l) => l.filter((x) => x._id !== m._id));
    await api(`/api/memories/${m._id}`, { method: 'DELETE' }).catch((e) =>
      setError(e.message)
    );
  };

  const K = KINDS[kind];
  const kinds = teacher
    ? ['preference']
    : ['health', 'joined', 'absence', 'left', 'note'];

  return (
    <Box>
      {title !== null && (
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
          {title || (en ? 'Memory' : 'Память')}
        </Typography>
      )}
      {!compact && (
        <Typography
          sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 1.25 }}
        >
          {teacher
            ? en
              ? 'The assistant keeps this in mind in every conversation.'
              : 'Помощник помнит это в каждом разговоре.'
            : en
              ? 'Every report, homework and the assistant take this into account. «Joined», «ill» and «left» dates also change attendance.'
              : 'Учитывается во всех отчётах, домашках и в ответах помощника. Даты «пришёл», «болел», «ушёл» меняют и посещаемость.'}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gap: 1, mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {!teacher && (
            <TextField
              select
              size="small"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              sx={{ minWidth: 190 }}
            >
              {kinds.map((k) => (
                <MenuItem key={k} value={k}>
                  {en ? KINDS[k].en : KINDS[k].ru}
                </MenuItem>
              ))}
            </TextField>
          )}
          {K.dates && (
            <TextField
              size="small"
              type="date"
              label={
                kind === 'left'
                  ? en
                    ? 'Since'
                    : 'С какого дня'
                  : en
                    ? 'From'
                    : 'С'
              }
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          )}
          {K.dates === 'range' && (
            <TextField
              size="small"
              type="date"
              label={en ? 'To' : 'По'}
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder={`${en ? 'e.g.' : 'например:'} ${en ? K.hint.en : K.hint.ru}`}
            inputProps={{ maxLength: 600 }}
          />
          <MicButton
            en={en}
            onText={(t) => t && setText((c) => (c ? `${c} ${t}` : t))}
            onError={(e) => setError(e.message)}
          />
          <Button
            variant="contained"
            onClick={add}
            disabled={busy || !text.trim()}
            sx={{ flexShrink: 0 }}
          >
            {en ? 'Remember' : 'Запомнить'}
          </Button>
        </Box>
      </Box>
      {error && (
        <Typography sx={{ color: 'error.main', fontSize: '0.85rem', mb: 1 }}>
          {error}
        </Typography>
      )}
      {!items ? (
        <CircularProgress size={20} />
      ) : !items.length ? (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          {en ? 'Nothing yet.' : 'Пока ничего.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 0.75 }}>
          {items.map((m) => (
            <MemoryItem key={m._id} m={m} en={en} onDelete={remove} />
          ))}
        </Box>
      )}
    </Box>
  );
}
